import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material'
import { useAppSelector } from 'appState/store'
import { theme } from 'components/Theme'
import { useEffect, useState } from 'react'
import { FaInfoCircle } from 'react-icons/fa'
import { Button, Tooltip } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { PositionEnum } from 'types/cssTypes'
import { notificator } from 'utils/notificator/notificator'

import { getUserPreference, settingsServiceApiPaths, setUserPreference } from '../settingsApi'
import { UserPreferencesKeysEnum } from '../settingsTypes'
import { SettingsAutocheckEnum } from '../types/settingsEnum'
import { SettingsAutoCheckConfig } from './SettingsAutoCheckConfig'

interface SelectedValues {
  [key: string]: string
}

export const SettingsAutoCheck = () => {
  const [selectedValues, setSelectedValues] = useState<SelectedValues>({})
  const [hasChanges, setHasChanges] = useState(false)
  const [initialValues, setInitialValues] = useState<SelectedValues>({})
  const { currentCustomer, eobEnabled, eobFaxNumber } = useAppSelector((state) => state.auth)

  const handleSaveChanges = async () => {
    // Send the selectedValues object to the API
    await setUserPreference(settingsServiceApiPaths.customerPreferencesPut(), {
      key: UserPreferencesKeysEnum.AutoCheckPreferences,
      value: JSON.stringify(selectedValues),
      customerId: currentCustomer,
    })
    setInitialValues(selectedValues)
    notificator.notify({ type: 'success', heading: 'Your AutoCheck settings were successfully updated' })
    //notificator.success('Your AutoCheck settings were successfully updated')
    setHasChanges(false)
  }

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>, sectionKey: string) => {
    const { value } = event.target
    setSelectedValues((prevState) => ({
      ...prevState,
      [sectionKey]: value,
    }))
  }

  useEffect(() => {
    const hasChanges = Object.keys(selectedValues).some((key) => selectedValues[key] !== initialValues[key])
    setHasChanges(hasChanges)
  }, [selectedValues, initialValues])

  useEffect(() => {
    let isValueEmpty = false
    getUserPreference(
      settingsServiceApiPaths.customerPreferencesGet(UserPreferencesKeysEnum.AutoCheckPreferences),
    ).then((response) => {
      const selectedValues = JSON.parse(response.value || '{}')

      isValueEmpty = (!selectedValues?.EOB || !selectedValues?.AutoCheck || !selectedValues?.AutoCheckCalls) ?? true

      const setValues = {
        [SettingsAutocheckEnum.EOB]: selectedValues?.EOB ? selectedValues?.EOB : 'always',
        [SettingsAutocheckEnum.AutoCheck]: selectedValues?.AutoCheck ? selectedValues?.AutoCheck : 'always',
        [SettingsAutocheckEnum.AutoCheckCalls]: selectedValues?.AutoCheckCalls
          ? selectedValues?.AutoCheckCalls
          : 'always',
      }

      if (isValueEmpty) {
        setUserPreference(settingsServiceApiPaths.customerPreferencesPut(), {
          key: UserPreferencesKeysEnum.AutoCheckPreferences,
          value: JSON.stringify(setValues),
          customerId: currentCustomer,
        })
      }
      setSelectedValues(setValues)
      setInitialValues(setValues)
    })
  }, [])

  return (
    <Div>
      <div>
        <div className="title">
          <h4>AutoCheck</h4>
        </div>
        <div>
          {eobEnabled && eobFaxNumber !== ''
            ? Object.entries(SettingsAutoCheckConfig).map(([key, value]) => (
                <div key={key}>
                  {key === SettingsAutocheckEnum.AutoCheck && <div className="advancedHolder">Advanced</div>}
                  <h6>
                    {value.title}{' '}
                    {value.tooltip !== '' && (
                      <div className="iconHolder">
                        <Tooltip
                          tooltipText={
                            key === SettingsAutocheckEnum.AutoCheckCalls ? (
                              <>
                                Allow AutoCheck to call again on re-uploaded claims that were previously called on or
                                are actively being called on.
                                <br />
                                <br /> Claim’s Unique IDs are used to identify existing call history.
                              </>
                            ) : (
                              value.tooltip
                            )
                          }
                          position={PositionEnum.right}
                          positionTooltipArrow={20}
                          tooltipStyles={{
                            minWidth: '30rem',
                            transform: 'translateY(-15%) translateX(10%)',
                          }}
                        >
                          <FaInfoCircle />
                        </Tooltip>
                      </div>
                    )}
                  </h6>
                  <FormControl component="fieldset">
                    <RadioGroup
                      name={`radioGroup-${key}`}
                      value={selectedValues[key] || ''}
                      onChange={(event) => handleRadioChange(event, key)}
                    >
                      {value.item.map((item, index) => (
                        <div className="inputholder" key={index}>
                          <FormControlLabel value={item.value} control={<Radio />} label={item.title} />
                          {item.tooltip !== '' && (
                            <div className="iconHolder">
                              <Tooltip
                                tooltipText={item.tooltip}
                                position={PositionEnum.right}
                                positionTooltipArrow={20}
                                tooltipStyles={{
                                  minWidth: '30rem',
                                  transform: 'translateY(-15%) translateX(10%)',
                                }}
                              >
                                <FaInfoCircle />
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                </div>
              ))
            : Object.entries(SettingsAutoCheckConfig)
                .filter(
                  ([key]) => key === SettingsAutocheckEnum.AutoCheck || key === SettingsAutocheckEnum.AutoCheckCalls,
                )
                .map(([key, value]) => (
                  <div key={key}>
                    {key === SettingsAutocheckEnum.AutoCheck && <div className="advancedHolder">Advanced</div>}
                    <h6>
                      {value.title}
                      {value.tooltip !== '' && (
                        <div className="iconHolder">
                          <Tooltip
                            tooltipText={value.tooltip}
                            position={PositionEnum.right}
                            positionTooltipArrow={20}
                            tooltipStyles={{
                              width: '18rem',
                              transform: 'translateY(-15%) translateX(11%)',
                            }}
                            hideTimeout={0}
                          >
                            <FaInfoCircle />
                          </Tooltip>
                        </div>
                      )}
                    </h6>
                    <FormControl component="fieldset">
                      <RadioGroup
                        name={`radioGroup-${key}`}
                        value={selectedValues[key] || ''}
                        onChange={(event) => handleRadioChange(event, key)}
                      >
                        {value.item.map((item, index) => (
                          <div className="inputholder" key={index}>
                            <FormControlLabel value={item.value} control={<Radio />} label={item.title} />
                            {item.tooltip !== '' && (
                              <div className="iconHolder">
                                <Tooltip
                                  tooltipText={item.tooltip}
                                  position={PositionEnum.right}
                                  positionTooltipArrow={30}
                                  tooltipStyles={{
                                    width: '18rem',
                                    transform: 'translateY(-15%) translateX(10%)',
                                  }}
                                  hideTimeout={0}
                                >
                                  <FaInfoCircle />
                                </Tooltip>
                              </div>
                            )}
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </div>
                ))}
        </div>
        <div className="buttonHolder">
          <Button variant="primary" onClick={handleSaveChanges} disabled={!hasChanges}>
            Save Changes
          </Button>
        </div>
      </div>
    </Div>
  )
}

const Div = styled.div`
  h4 {
    ${({ theme }) => theme.typography.h4.medium};
    color: ${theme.colors.neutral.dark};
    border-bottom: 1px solid ${theme.colors.grey.lighter};
    padding: ${theme.spacing.l} 0;
  }

  h6 {
    display: flex;
    ${({ theme }) => theme.typography.h6.medium};
    color: ${theme.colors.neutral.neutral};
    padding: ${theme.spacing.l} 0 0;
    gap: ${theme.spacing.l};
    .iconHolder {
      svg {
        color: ${theme.colors.grey.light};
      }
    }
  }

  .inputholder {
    display: flex;
    align-items: center;
    .iconHolder {
      svg {
        color: ${theme.colors.grey.light};
      }
    }
  }

  .advancedHolder {
    ${({ theme }) => theme.typography.xs.medium};
    color: ${theme.colors.grey.neutral};
    margin-top: ${theme.spacing.xxxl};
  }

  .advancedHolder:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 0.5em;
    border-top: 1px solid black;
    z-index: -1;
  }

  label {
    ${({ theme }) => theme.typography.m.normal};
    color: ${theme.colors.grey.darker};
  }

  .buttonHolder {
    display: flex;
    flex-direction: row-reverse;
    justify-content: flex-end;
    padding: ${theme.spacing.xxl} 0;
    margin-top: ${theme.spacing.xxxl};
  }
`
